import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { ProTable, Link, DateDisplay } from 'components/layout';
import { GET_PROJECT_APPLICATIONS } from 'gql/projectApplications';
import { GET_PROJECT_APPLICATION_SETTINGS } from 'gql/projectApplicationSetting';
import { filterByActiveKey } from 'utils';
import { ENUM_PROJECT_APPLICATION_STATUS } from 'constants/form';

const url = (id) => `/project-applications/${id}`;

const getFunding = (settings) => {
  if (!settings) return {};
  return settings?.reduce(
    (prev, { id, title }) => ({ ...prev, [id]: title }),
    {}
  );
};
const enumFundingStatus = {
  ALL: 'All',
  OPEN: 'Open',
  CLOSED: 'Closed',
};
const enumStatus = {
  ALL: ENUM_PROJECT_APPLICATION_STATUS.ALL,
  ACTIVE: ENUM_PROJECT_APPLICATION_STATUS.ACTIVE,
  PENDING: ENUM_PROJECT_APPLICATION_STATUS.PENDING,
  APPROVED: ENUM_PROJECT_APPLICATION_STATUS.APPROVED,
  OVERDUE: ENUM_PROJECT_APPLICATION_STATUS.OVERDUE,
};
const getStatus = (status) => {
  if (!status || status === 'ALL' || status === 'ACTIVE') {
    return ['PENDING', 'APPROVED'];
  }
  if (status === 'OVERDUE') {
    return ['PENDING'];
  }
  return [status];
};
const getFundingStatus = (showPriority) => {
  switch (showPriority) {
    case 'OPEN':
      return [0];
    case 'CLOSED':
      return [2];
    default:
      return [0, 2];
  }
};
const PendingProjectApplication = () => {
  const { data, loading, refetch } = useQuery(GET_PROJECT_APPLICATIONS, {
    variables: {
      ignoreNotAuthorised: true,
      showPriorities: [0],
      status: ['PENDING'],
    },
    errorPolicy: 'all',
  });
  const { data: settingData, loading: settingLoading } = useQuery(
    GET_PROJECT_APPLICATION_SETTINGS,
    {
      variables: {
        showPriorities: [0],
      },
    }
  );
  const [activeKey, setActiveKey] = useState('mine');

  const columns = [
    {
      title: 'Funding',
      key: 'setting',
      valueType: 'select',
      colSize: 2,
      valueEnum: getFunding(settingData?.projectApplicationSettings),
      dataIndex: 'setting',
      // sorter: true,
      render: (_, { setting, id }) => <Link to={url(id)}>{setting.title}</Link>,
    },
    {
      title: 'Title',
      colSize: 2,
      key: 'keyword',
      hideInTable: true,
    },
    {
      title: 'Funding status',
      key: 'showPriority',
      dataIndex: ['setting', 'showPriority'],
      valueEnum: enumFundingStatus,
      hideInTable: true,
      initialValue: 'OPEN',
    },
    {
      title: 'Application deadline',
      key: 'endDate',
      dataIndex: 'endDate',
      // sorter: (a, b) => a.setting?.endDate - b.setting?.endDate,
      render: (_, { setting, id }) => (
        <>
          <Link to={url(id)}>
            <DateDisplay date={setting.endDate} />
          </Link>
        </>
      ),
      hideInSearch: true,
    },
    {
      title: 'Applicant',
      key: 'user',
      dataIndex: 'user',
      hideInSearch: true,
      // sorter: true,
      render: (_, { user, id }) => (
        <>
          <Link to={url(id)}>{user?.name}</Link>
        </>
      ),
    },
    {
      title: 'Faculty',
      key: 'faculty',
      dataIndex: 'faculty',
      hideInSearch: true,
      // sorter: true,
      render: (_, { hostDepartment, id }) => (
        <>
          <Link to={url(id)}>{hostDepartment.faculty.abbreviation}</Link>
        </>
      ),
    },
    {
      title: 'Department',
      key: 'department',
      dataIndex: 'department',
      hideInSearch: true,
      // sorter: true,
      render: (_, { hostDepartment, id }) => (
        <>
          <Link to={url(id)}>{hostDepartment.abbreviation}</Link>
        </>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      hideInSearch: true,
      width: '30%',
      // sorter: true,
      render: (_, { id }) => <Link to={url(id)}>{_}</Link>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: enumStatus,
      initialValue: 'PENDING',
      // sorter: true,
      render: (_, { id }) => <Link to={url(id)}>{_}</Link>,
      defaultSortOrder: 'descend',
    },
  ];
  const activeKeyFilter = filterByActiveKey(activeKey);

  return (
    <ProTable
      loading={settingLoading || loading}
      toolbar={{
        menu: {
          type: 'inline',
          activeKey,
          items: [
            {
              key: 'mine',
              label: <span>Need your endorsement</span>,
            },
            {
              key: 'all',
              label: <span>All</span>,
            },
          ],
          onChange: (key) => {
            setActiveKey(key);
          },
        },
        settings: false,
      }}
      request={async (params, _sort, _filter) => {
        try {
          const filters = activeKeyFilter;
          const result = await refetch({
            setting: params.setting,
            showPriorities: getFundingStatus(params?.showPriority),
            status: getStatus(params?.status),
            keyword: params.keyword,
          });
          const dataSource = filters(result?.data?.projectApplications);
          return Promise.resolve({
            data: dataSource,
            success: true,
          });
        } catch (error) {}
      }}
      search={{
        searchText: 'Search',
        labelWidth: 'auto',
        collapsed: false,
        collapseRender: false,
      }}
      columns={columns}
      rowKey="id"
      pagination={{
        defaultPageSize: 20,
        showLessItems: true,
      }}
      dateFormatter="string"
      dataSource={activeKeyFilter(data?.projectApplications?.nodes)}
    />
  );
};

export default PendingProjectApplication;
